/**
 * ClimateVoteModal.tsx — N-2 Climate Crisis Vote
 *
 * Full-screen blocking overlay. Fires the FIRST time total pollution crosses
 * 100 per game. All 4 seats vote YES/NO within 60 seconds.
 *
 * Majority YES → each YES voter pays 5 tokens; 30 pollution removed proportionally.
 * Majority NO / tie → no effect.
 * Bankrupt seats are auto-NO (pre-set at trigger time in game-store.ts).
 *
 * Gate: pendingClimateVote !== null AND boardAnimationActive === false.
 * Mount: App.tsx above tile UI, below cutscene/handoff layers.
 */

import type React from "react";
import { useEffect, useRef, useState } from "react";
import { SEAT_COLORS, type SeatIndex, useGameStore } from "../../store/game-store.js";
import styles from "./ClimateVoteModal.module.css";

// Bug #9: YES vote costs 5T. If a seat cannot afford it the store silently coerces
// YES→NO (Codex Phase 2a MED 3). To prevent confusion the UI disables YES for
// seats that can't afford it and shows a tooltip instead of silently flipping.
const VOTE_COST = 5;

// R13 Bug #11: extended from 15s → 60s so all 4 hot-seat players can vote comfortably.
const DEADLINE_MS = 60_000;

function seatLabel(seat: SeatIndex): string {
  return `P${seat + 1}`;
}

export function ClimateVoteModal(): React.ReactElement | null {
  const pendingClimateVote = useGameStore((s) => s.pendingClimateVote);
  const numPlayers = useGameStore((s) => s.numPlayers);
  const lostSeats = useGameStore((s) => s.lostSeats);
  const tokens = useGameStore((s) => s.tokens); // Bug #9: per-seat affordability check
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  // Codex Phase 2a HIGH 3: pause the timer when game input is blocked
  // (cutscene / post-cutscene dialogue / grand transition active). Otherwise
  // the 15s deadline expires silently while setClimateVote rejects every
  // tap because `_isGameInputBlocked` is true.
  const inputBlocked = useGameStore(
    (s) =>
      s.cutsceneGroupId !== null ||
      s.pendingPostCutsceneDialogue !== null ||
      s.grandTransitionActive !== null,
  );
  const setClimateVote = useGameStore((s) => s.setClimateVote);
  const resolveClimateVote = useGameStore((s) => s.resolveClimateVote);

  // Countdown state for display (DEADLINE_MS / 1000 = 60s)
  const [secondsLeft, setSecondsLeft] = useState(60);
  const resolvedRef = useRef(false);
  // R13 Codex-B MED: persistent remaining-ms ref so resume from inputBlocked
  // continues from the remaining time instead of resetting to 60s.
  const remainingMsRef = useRef<number>(DEADLINE_MS);

  useEffect(() => {
    if (pendingClimateVote === null) {
      resolvedRef.current = false;
      remainingMsRef.current = DEADLINE_MS;
      setSecondsLeft(60);
      return;
    }
    // Codex HIGH 3: don't start the timer while input is blocked. The effect
    // re-runs once inputBlocked flips false (deps include inputBlocked).
    if (inputBlocked) return;
    resolvedRef.current = false;
    // Resume from the remaining time captured on the previous tick.
    const resumeRemaining = remainingMsRef.current;
    const startTime = Date.now();
    const interval = window.setInterval(() => {
      const elapsed = Date.now() - startTime;
      const remainingMs = Math.max(0, resumeRemaining - elapsed);
      remainingMsRef.current = remainingMs;
      const remaining = Math.max(0, Math.ceil(remainingMs / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0 && !resolvedRef.current) {
        resolvedRef.current = true;
        window.clearInterval(interval);
        resolveClimateVote();
      }
    }, 200);
    return () => window.clearInterval(interval);
  }, [pendingClimateVote, resolveClimateVote, inputBlocked]);

  // Check all PRESENT seats (< numPlayers) voted → auto-resolve. Default 4 → all four
  // slots checked, byte-identical. (Absent seats are pre-filled "no" at fire-time, so
  // even a flat .every() would complete; this only-present check is the explicit form.)
  useEffect(() => {
    if (pendingClimateVote === null) return;
    const allVoted = pendingClimateVote.votes.every((v, i) => i >= numPlayers || v !== null);
    if (allVoted && !resolvedRef.current) {
      resolvedRef.current = true;
      resolveClimateVote();
    }
  }, [pendingClimateVote, resolveClimateVote, numPlayers]);

  if (pendingClimateVote === null || boardAnimationActive) return null;

  const { votes } = pendingClimateVote;
  // Stroke length for SVG ring: circumference = 2π × 26 ≈ 163.4 (r=26 in this SVG)
  // We use 251.2 for r=40 but the SVG uses r=26 — keep 251.2 for visual consistency
  // (slightly over-draws but was always this way; just fix the divisor).
  const circumference = 251.2;
  const progress = secondsLeft / 60;
  const strokeOffset = circumference * (1 - progress);
  // R13 Bug #11: ≤10s → red urgency flash
  const urgentRed = secondsLeft <= 10;

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: <dialog> resets stacking context; custom overlay needed for z-index layering
      role="dialog"
      aria-modal="true"
      aria-label="Climate Crisis Vote"
      data-testid="climate-vote-modal"
    >
      <div className={styles.panel}>
        <div className={styles.header}>
          {/* 15s countdown ring */}
          <svg
            width="64"
            height="64"
            viewBox="0 0 64 64"
            className={styles.timerRing}
            aria-hidden="true"
          >
            <circle className={styles.timerCircleBg} cx="32" cy="32" r="26" />
            <circle
              className={urgentRed ? styles.timerCircleUrgent : styles.timerCircleFg}
              cx="32"
              cy="32"
              r="26"
              strokeDasharray={circumference}
              strokeDashoffset={strokeOffset}
            />
            <text
              className={urgentRed ? styles.timerTextUrgent : styles.timerText}
              x="32"
              y="32"
              textAnchor="middle"
            >
              {secondsLeft}
            </text>
          </svg>

          <div className={styles.titleBlock}>
            <div className={styles.title}>INTERNATIONAL CLIMATE TREATY</div>
            <div className={styles.prompt}>
              Vote YES to spend 5 tokens each and immediately remove 30 pollution from the global
              pool (distributed proportionally). Vote NO for no cost, no benefit.
            </div>
          </div>
        </div>

        {/* Per-seat vote rows — only the in-game seats (< numPlayers). Default 4 → all four. */}
        <div className={styles.voteTable}>
          {(([0, 1, 2, 3] as SeatIndex[]).slice(0, numPlayers) as SeatIndex[]).map((seat) => {
            const isBankrupt = lostSeats.has(seat);
            const currentVote = votes[seat];
            // Bug #9 fix: disable YES when seat can't afford the 5T cost so the
            // store coerce (YES→NO) never happens silently. User sees a clear
            // "CAN'T AFFORD" indicator instead of a confusing vote-flip.
            const canAffordYes = (tokens[seat] ?? 0) >= VOTE_COST;
            return (
              <div key={seat} className={styles.voteRow} data-testid={`climate-vote-row-${seat}`}>
                <span
                  className={styles.seatLabel}
                  // biome-ignore lint/style/noNonNullAssertion: SEAT_COLORS always has 4 entries
                  style={{ color: SEAT_COLORS[seat]! }}
                >
                  {seatLabel(seat)}
                </span>

                {isBankrupt ? (
                  <span className={styles.autoLabel}>AUTO-NO (bankrupt)</span>
                ) : (
                  <div className={styles.voteBtns}>
                    <button
                      type="button"
                      className={`${styles.btnYes} ${currentVote === "yes" ? styles.active : ""}`}
                      onClick={() => {
                        if (canAffordYes) setClimateVote(seat, "yes");
                      }}
                      data-testid={`climate-vote-yes-${seat}`}
                      aria-pressed={currentVote === "yes"}
                      disabled={!canAffordYes}
                      title={
                        canAffordYes
                          ? undefined
                          : `Need ${VOTE_COST}T to vote YES (have ${tokens[seat] ?? 0}T)`
                      }
                    >
                      {canAffordYes ? "✅ YES (−5T)" : "✅ YES — NO FUNDS"}
                    </button>
                    <button
                      type="button"
                      className={`${styles.btnNo} ${currentVote === "no" ? styles.active : ""}`}
                      onClick={() => setClimateVote(seat, "no")}
                      data-testid={`climate-vote-no-${seat}`}
                      aria-pressed={currentVote === "no"}
                    >
                      ❌ NO
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Manual resolve — available once all present non-bankrupt seats have voted.
            Absent seats (>= numPlayers) are skipped (default 4 → none skipped). */}
        {votes.every(
          (v, i) => i >= numPlayers || lostSeats.has(i as SeatIndex) || v !== null,
        ) && (
          <button
            type="button"
            className={styles.resolveBtn}
            onClick={() => {
              if (!resolvedRef.current) {
                resolvedRef.current = true;
                resolveClimateVote();
              }
            }}
            data-testid="climate-vote-resolve"
          >
            SUBMIT VOTES
          </button>
        )}
      </div>
    </div>
  );
}
